import React from 'react';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { amber, blue, blueGrey, green, red } from '@mui/material/colors';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridOverlay,
  GridValueGetterParams
} from '@mui/x-data-grid';
import PanToolIcon from '@mui/icons-material/PanTool';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  DateRange,
  RequestGridHeader,
  RequestGridRow
} from '../../models';

import {
  EMPTY_STRING,
  SignupStatusID
} from '../../global';

interface NightsMobileProps {
  dateRange: DateRange;
  headers: Array<RequestGridHeader>;
  isLoading: boolean;
  nights: number;
  rows: Array<RequestGridRow>;
}

const NightsMobile: React.FC<NightsMobileProps> = (props) => {

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    cancelled: {
      color: blue[500]
    },
    cancelledlate: {
      color: red[500]
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0)
    },
    pending: {
      color: blueGrey[500]
    },
    waitlist: {
      color: amber[500]
    }
  };

  // #endregion

  // #region Methods

  /*
 * Grid column definitions
 */
  const columns = (): Array<GridColDef> => {

    return [
      {
        field: 'fullName',
        headerName: 'Member/Guest',
        width: 150,
        type: 'string',
        renderCell: (params: GridCellParams) => (
          renderName(params)
        )
      }, {
        field: 'arrivalDate',
        headerName: 'Dates',
        width: 110,
        type: 'string',
        renderCell: (params: GridCellParams) => (
          renderDates(params)
        )
      }, {
        field: 'statusID',
        headerName: '',
        align: 'center',
        width: 40,
        type: 'number',
        renderCell: (params: GridCellParams) => (
          renderStatus(params)
        )
      }
    ];

  }

  /*
   * Overlay displayed when no data
   */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No request nights for the selected date range</div>
      </GridOverlay>
    );
  }

  /*
   * Render grid cell date
   */
  const renderDates = (params: GridValueGetterParams) => {
    const a = new Date(params.row.arrivalDate);
    const d = new Date(params.row.departureDate);

    return `${a.getMonth() + 1}/${a.getDate()}-${d.getMonth() + 1}/${d.getDate()}`;
  }

  /*
   * Render name for row
   */
  const renderName = (params: GridCellParams) => {
    const isG = params.row.isGuest as boolean;

    if (isG) {
      return (
        <Tooltip title={`Guest of ${params.row.guestOf || ''}`} arrow>
          <span>{params.row.rank}-{params.row.lastName || ''}, {params.row.firstName || ''}{params.row.priorityGuest ? '*' : EMPTY_STRING}</span>
        </Tooltip>
      );
    } else {
      return (
        <span>{params.row.rank}-{params.row.lastName || ''}, {params.row.firstName || ''}</span>
      );
    }

  }

  /*
* Render the cell for signup status
*/
  const renderStatus = (params: GridCellParams) => {

    const id = params.value as number;

    switch (id) {
      case SignupStatusID.Requested:
        return (
          <PendingIcon fontSize="medium" sx={styles.pending} />
        )
      case SignupStatusID.Confirmed:
        return (
          <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
        )
      case SignupStatusID.Waitlist:
        return (
          <PanToolIcon fontSize="medium" sx={styles.waitlist} />
        )
      case SignupStatusID.Denied:
        return (
          <BlockIcon fontSize="medium" sx={styles.deined} />
        )
      case SignupStatusID.Cancelled:
        return (
          <CancelIcon fontSize="medium" sx={styles.cancelled} />
        )
      case SignupStatusID.CancelledLate:
        return (
          <CancelIcon fontSize="medium" sx={styles.cancelledlate} />
        )
      default:
        return (<span />)
    }

  }

  // #endregion

  return (
    <DataGrid
      columns={columns()}
      rows={props.rows}
      getRowId={(row) => row.rowID} // Special unique ID for each row
      sx={styles.root}
      density="compact"
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      loading={props.isLoading}
      slots={{
        noRowsOverlay: noRowsOverlay
      }}
    />
  );

}

export default NightsMobile;